import { Moment } from 'moment';
import React from 'react';
import { createThirdPartyDeeplink } from '../../../../queries/ats/common';
import { useProviders } from '../../../../queries/ats/me/providers/hooks';
import { Provider, ThirdPartyProvider } from '../../../../queries/ats/me/providers/types';
import { routes } from '../../../../routes/route-utils';
import { DetailsLink } from '../../../../views/ats-details/details-link';
import { ThirdPartyDeepLink } from '../../../ATS/ats-deep-links/third-party-deep-link';
import { Anchor, Link } from '../../../Link';
import './ProgramHeaderStyle.scss';

function isNoProgram(name: string) {
  const lowerCaseName = name.toLowerCase();
  return lowerCaseName === 'no program' || lowerCaseName === 'ingen sending' || lowerCaseName === 'ingen forsendelse';
}

interface Props {
  id: string;
  name: string;
  channelId: string;
  startTime: Moment | undefined;
  className?: string;
  episodeNumber: string | undefined;
  markers: JSX.Element;
  canPlay: boolean;
  extensionInfo: {
    key: string;
    value: string;
  }[];
}

export default function ProgramHeader({
  id,
  name,
  channelId,
  startTime,
  episodeNumber,
  className,
  markers,
  canPlay,
  extensionInfo,
}: Props) {
  const startTimeTitle = startTime ? startTime.calendar() : undefined;
  const programStartTime = startTime ? startTime.format('HH:mm') : '';
  const programName = name;
  const seasonAndEpisode = episodeNumber ? `- ${episodeNumber}` : null;
  const programClassName = 'program-info' + (className ? ` ${className}` : '');

  const { data: providers } = useProviders();
  const provider = getThirdPartyProvider(providers || [], channelId);

  if (isNoProgram(name)) {
    return (
      <DetailsLink type="program" id="empty" params={{ platformTitleId: channelId }} className={programClassName}>
        <div className="startTime" title={startTimeTitle}>
          {programStartTime}
        </div>
        <div className="name-episode-season-wrapper">
          <div className="program-name">{programName}</div>
          <div className="season-and-episode">&nbsp;{seasonAndEpisode}</div>
        </div>
        <div className="controls-container">
          <ul className="controls">{markers}</ul>
        </div>
      </DetailsLink>
    );
  }

  if (provider === undefined) {
    const route = routes.play({ platformTitleId: id, providerIdentifier: channelId });

    return (
      <div className={programClassName}>
        <div className="startTime" title={startTimeTitle}>
          {programStartTime}
        </div>
        <DetailsLink
          type="program"
          id={id}
          params={{ platformTitleId: id, orderByProviderId: channelId }}
          className="name-episode-season-wrapper"
        >
          <div className="program-name">{programName}</div>
          <div className="season-and-episode">&nbsp;{seasonAndEpisode}</div>
        </DetailsLink>
        <div className="controls-container">
          <ul className="controls">
            {markers}
            <div className="catchup-button">
              {canPlay && (
                <li>
                  <Link to={route}>z</Link>
                </li>
              )}
            </div>
          </ul>
        </div>
      </div>
    );
  }

  const catchupLink = extensionInfo.find((info) => info.key === 'CATCHUP_LINK');

  if (catchupLink === undefined) {
    return (
      <DetailsLink
        type="program"
        id={id}
        params={{ platformTitleId: id, orderByProviderId: channelId }}
        className={programClassName}
      >
        <div className="startTime" title={startTimeTitle}>
          {programStartTime}
        </div>
        <div className="name-episode-season-wrapper">
          <div className="program-name">{programName}</div>
          <div className="season-and-episode">&nbsp;{seasonAndEpisode}</div>
        </div>
        <div className="controls-container">
          <ul className="controls">
            {markers}
            <div className="catchup-button">{canPlay && <li>z</li>}</div>
          </ul>
        </div>
      </DetailsLink>
    );
  }

  const url = createThirdPartyDeeplink(provider, catchupLink.value);

  return (
    <div className={programClassName}>
      <div className="startTime" title={startTimeTitle}>
        {programStartTime}
      </div>
      <DetailsLink
        type="program"
        id={id}
        params={{ platformTitleId: id, orderByProviderId: channelId }}
        className="name-episode-season-wrapper"
      >
        <div className="program-name">{programName}</div>
        <div className="season-and-episode">&nbsp;{seasonAndEpisode}</div>
      </DetailsLink>
      <div className="controls-container">
        <ul className="controls">
          {markers}
          <div className="catchup-button">
            {canPlay && (
              <li>
                <ThirdPartyDeepLink
                  type={provider.params.popupTextType}
                  link={url}
                  providerIdentifier={provider.id}
                  providerName={provider.name}
                >
                  <Anchor href={url}>z</Anchor>
                </ThirdPartyDeepLink>
              </li>
            )}
          </div>
        </ul>
      </div>
    </div>
  );
}

function getThirdPartyProvider(providers: Provider[], channelId: string): ThirdPartyProvider | undefined {
  return providers.find((provider): provider is ThirdPartyProvider => {
    if (provider.type !== 'thirdParty') {
      return false;
    }

    if (provider.params.channelIds === undefined) {
      return false;
    }

    return provider.params.channelIds.includes(channelId);
  });
}
